import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect, useState } from 'react';
import { useStableFunction } from '../../core/hooks/use-stable-function';
import isNil from '../../core/helpers/is-nil';
import { WEBSITE_ID } from '../../core/config';
import { getLoggerInstance } from '../../core/monitoring/logger';
import { initUmami } from './initUmami';
import { identify } from './track';

const logger = getLoggerInstance('analytics/tracker');

const globalTagManagerStateSingleton = {
  isInitialized: false,
};

/**
 * Extracts the domain part (e.g. "@google.com") of an email address.
 * @param email
 */
function getEmailDomain(email: string): string | undefined {
  const atIndex = email.lastIndexOf('@');
  return atIndex === -1 ? undefined : email.slice(atIndex);
}

const AnalyticsTracker: React.FC = () => {
  const { user } = useAuth0();
  const [isUmamiInitialized, setIsUmamiInitialized] = useState(false);

  const initializeTracker = useStableFunction(async () => {
    if (globalTagManagerStateSingleton.isInitialized || isNil(WEBSITE_ID)) {
      return;
    }
    globalTagManagerStateSingleton.isInitialized = true;
    try {
      await initUmami(WEBSITE_ID);
      setIsUmamiInitialized(true);
    } catch (exception) {
      logger.debug('Could not initialize Umami analytics.', { exception });
    }
  });

  useEffect(() => {
    initializeTracker();
  }, [initializeTracker]);

  useEffect(
    function identifyUser() {
      if (!isUmamiInitialized) {
        return;
      }
      if (isNil(user?.email)) {
        return;
      }
      const emailDomain = getEmailDomain(user.email);
      if (isNil(emailDomain)) {
        logger.warn('Could not extract an email domain for analytics identification for user.', { user });
        return;
      }
      identify({ emailDomain });
    },
    [user, isUmamiInitialized],
  );

  return null;
};

export default AnalyticsTracker;
